import { Box, HStack } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FrameworkAgreementDto,
  FrameworkAgreementStatus,
  PanelStatus,
  useGetFrameworkAgreementQuery,
} from "../../../autogen/bff-api";
import { NewCallOffModal } from "../../framework-agreement/NewCallOffModal";
import { NewMiniCompetitionModal } from "../../framework-agreement/NewMiniCompetitionModal";
import { PanelActionButton } from "../../framework-agreement/PanelActionButton";
import { StartRefusalNote } from "../../framework-agreement/StartRefusalNote";
import { useStartRefusal } from "../../framework-agreement/callOffAvailability";

export const FrameworkContractActions = ({
  frameworkAgreementId,
  contractId,
}: {
  frameworkAgreementId: string;
  contractId: string;
}) => {
  const { t } = useTranslation();
  const offered = useOfferedFrameworkActions(frameworkAgreementId, contractId);
  const [starting, setStarting] = useState<"miniCompetition" | "callOff" | null>(null);

  if (!offered) return null;
  const { framework, supplier, offersMiniCompetition, offersCallOff } = offered;

  return (
    <HStack spacing="2">
        {offersMiniCompetition && (
          <PanelActionButton
            framework={framework}
            variant="outline"
            label={t("New mini-competition")}
            startsMiniCompetition
            onClick={() => setStarting("miniCompetition")}
          />
        )}
        {offersCallOff && (
          <PanelActionButton
            framework={framework}
            variant="outline"
            label={t("New call-off")}
            onClick={() => setStarting("callOff")}
          />
        )}
      {starting === "miniCompetition" && (
        <NewMiniCompetitionModal framework={framework} isOpen onClose={() => setStarting(null)} />
      )}
      {starting === "callOff" && (
        <NewCallOffModal
          framework={framework}
          supplierOrganizationId={supplier.organizationId}
          isOpen
          onClose={() => setStarting(null)}
        />
      )}
    </HStack>
  );
};

export const FrameworkContractStartRefusals = ({
  frameworkAgreementId,
  contractId,
}: {
  frameworkAgreementId: string;
  contractId: string;
}) => {
  const offered = useOfferedFrameworkActions(frameworkAgreementId, contractId);
  if (!offered) return null;
  return (
    <Box mt="1" textAlign={{ base: "left", md: "right" }}>
      <StartRefusalNotes
        framework={offered.framework}
        offersMiniCompetition={offered.offersMiniCompetition}
        offersCallOff={offered.offersCallOff}
      />
    </Box>
  );
};

const useOfferedFrameworkActions = (frameworkAgreementId: string, contractId: string) => {
  const { data: framework } = useGetFrameworkAgreementQuery({ frameworkAgreementId });
  if (!framework || framework.status !== FrameworkAgreementStatus.Active) return null;
  const supplier = (framework.panel ?? []).find((member) => member.contractId === contractId);
  if (!supplier || supplier.status !== PanelStatus.Active) return null;
  const offersMiniCompetition = framework.allowsMiniCompetitions && framework.mayOpenMiniCompetition === true;
  const offersCallOff = framework.allowsDirectAward && framework.mayCreateCallOff;
  if (!offersMiniCompetition && !offersCallOff) return null;
  return { framework, supplier, offersMiniCompetition, offersCallOff };
};

const StartRefusalNotes = ({
  framework,
  offersMiniCompetition,
  offersCallOff,
}: {
  framework: FrameworkAgreementDto;
  offersMiniCompetition: boolean;
  offersCallOff: boolean;
}) => {
  const callOffRefusal = useStartRefusal(framework);
  const miniCompetitionRefusal = useStartRefusal(framework, { startsMiniCompetition: true });
  const repeatsCallOffRefusal = offersCallOff && miniCompetitionRefusal === callOffRefusal;
  return (
    <>
      {offersMiniCompetition && !repeatsCallOffRefusal && (
        <StartRefusalNote framework={framework} startsMiniCompetition />
      )}
      {offersCallOff && <StartRefusalNote framework={framework} />}
    </>
  );
};
